import { ContextManager } from 'dreamstate';

import { AnyObject, Callable } from '@vimeo/core/types/generic';

/**
 * Grouped by name unSubscribers.
 * Allow to unsubscribe from only part of callbacks.
 */
export type TCategoryCallable = [string, Callable];

/**
 * Base manager that can handle subscribers-unSubscribers callback pairs bound to lifecycle.
 */
export abstract class UnsubscribingContextManager<T, D = AnyObject> extends ContextManager<T, D> {
  private eventUnSubscribers: Array<Callable | TCategoryCallable> = [];

  /**
   * Add unsubscribing listeners for lifecycle management.
   * Following callbacks will be called on context manager disposing.
   */
  public addUnSubscribers(subscribers: Array<Callable>, category?: string): void {
    if (category) {
      this.eventUnSubscribers = this.eventUnSubscribers.concat(
        subscribers.map((it: Callable) => [category, it]),
      );
    } else {
      this.eventUnSubscribers = this.eventUnSubscribers.concat(subscribers);
    }
  }

  /**
   * Dispose everything related to manager lifecycle.
   * Prevent updates/calls when it is removed from storage management system.
   */
  public disposeSubscribers(category?: string): void {
    if (category) {
      this.eventUnSubscribers = this.eventUnSubscribers.filter(
        (it: Callable | TCategoryCallable) => {
          if (Array.isArray(it) && it[0] === category) {
            it[1]();
            return false;
          } else {
            return true;
          }
        },
      );
    } else {
      this.eventUnSubscribers.forEach((it) => (Array.isArray(it) ? it[1]() : it()));
      this.eventUnSubscribers = [];
    }
  }

  /**
   * Automatically unsubscribe from listeners when provision ends.
   * Note: In most cases when extending provisionEnded method should also call super manually.
   */
  public override onProvisionEnded(): void {
    this.disposeSubscribers();
  }
}
